/**
 * DataSheet - Spreadsheet-style key-value display
 *
 * Pure, idempotent, compositional component for displaying structured data
 * in a horizontal table format that's easy to scan for spreadsheet-inclined users.
 *
 * Uses compound component pattern:
 * - DataSheet: Container with optional header
 * - DataSheet.Section: Grouping with optional header
 * - DataSheet.Row: Horizontal key-value pair
 *
 * @example
 * ```tsx
 * <DataSheet title="Contract Details">
 *   <DataSheet.Section title="Competition">
 *     <DataSheet.Row label="Extent Competed" value="Full and Open" code="A" />
 *     <DataSheet.Row label="Set Aside Type" value="Small Business" highlight />
 *   </DataSheet.Section>
 *   <DataSheet.Section title="Pricing">
 *     <DataSheet.Row label="Contract Type" value="Firm Fixed Price" code="J" />
 *   </DataSheet.Section>
 * </DataSheet>
 * ```
 */
import * as React from 'react'

import { ChevronDown, ChevronRight, Hash } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

// ============================================================================
// Types
// ============================================================================

type DataSheetVariant = 'default' | 'compact' | 'bordered'

interface DataSheetContextValue {
  variant: DataSheetVariant
}

interface DataSheetProps {
  children: React.ReactNode
  /** Card title */
  title?: string
  /** Icon to display next to title */
  icon?: React.ReactNode
  /** Badge to display in header */
  badge?: React.ReactNode
  /** Visual variant */
  variant?: DataSheetVariant
  className?: string
}

interface SectionProps {
  children: React.ReactNode
  /** Section header title */
  title?: string
  /** Icon for section header */
  icon?: React.ReactNode
  /** Make section collapsible */
  collapsible?: boolean
  /** Initial collapsed state (only when collapsible) */
  defaultCollapsed?: boolean
  className?: string
}

interface RowProps {
  /** Row label (left column) */
  label: string
  /** Row value (right column) */
  value?: React.ReactNode
  /** Optional code badge displayed after value */
  code?: string
  /** Highlight this row (e.g., for important values) */
  highlight?: boolean
  /** Truncate long values */
  truncate?: boolean
  className?: string
}

interface EmptyStateProps {
  children?: React.ReactNode
  className?: string
}

// ============================================================================
// Context
// ============================================================================

const DataSheetContext = React.createContext<DataSheetContextValue | null>(null)

function useDataSheetContext(): DataSheetContextValue {
  const context = React.useContext(DataSheetContext)
  if (!context) {
    throw new Error('DataSheet sub-components must be used within DataSheet')
  }
  return context
}

// ============================================================================
// Style Definitions
// ============================================================================

const containerStyles: Record<DataSheetVariant, string> = {
  default:
    'overflow-hidden rounded-lg border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900',
  compact:
    'overflow-hidden rounded-lg border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900',
  bordered:
    'overflow-hidden rounded-lg border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900',
}

const headerStyles: Record<DataSheetVariant, string> = {
  default:
    'border-b border-primary-100 bg-primary-50/50 px-4 py-3 dark:border-primary-800 dark:bg-primary-800/50',
  compact:
    'border-b border-primary-100 bg-primary-50/50 px-3 py-2 dark:border-primary-800 dark:bg-primary-800/50',
  bordered:
    'border-b border-primary-100 bg-primary-50/50 px-4 py-3 dark:border-primary-800 dark:bg-primary-800/50',
}

const rowPaddingStyles: Record<DataSheetVariant, string> = {
  default: 'px-4 py-2.5',
  compact: 'px-3 py-1.5',
  bordered: 'px-4 py-2.5',
}

// ============================================================================
// Sub-Components
// ============================================================================

/**
 * DataSheet.Section - Grouping with optional header
 */
const Section: React.FC<SectionProps> = ({
  children,
  title,
  icon,
  collapsible = false,
  defaultCollapsed = false,
  className,
}) => {
  const [isCollapsed, setIsCollapsed] = React.useState(defaultCollapsed)

  const handleToggle = React.useCallback(() => {
    if (collapsible) {
      setIsCollapsed((prev) => !prev)
    }
  }, [collapsible])

  const headerContent = title && (
    <div
      className={twMerge(
        'flex items-center gap-1.5 border-y border-primary-200 bg-primary-100/80 px-4 py-2 dark:border-primary-700 dark:bg-primary-800/80',
        collapsible &&
          'cursor-pointer select-none hover:bg-primary-100 dark:hover:bg-primary-800',
      )}
      onClick={handleToggle}
      role={collapsible ? 'button' : undefined}
      tabIndex={collapsible ? 0 : undefined}
      onKeyDown={
        collapsible
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleToggle()
              }
            }
          : undefined
      }
      aria-expanded={collapsible ? !isCollapsed : undefined}
    >
      {collapsible && (
        <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center text-primary-500 dark:text-primary-400">
          {isCollapsed ? (
            <ChevronRight className="h-full w-full" />
          ) : (
            <ChevronDown className="h-full w-full" />
          )}
        </span>
      )}
      {icon ? (
        <span className="ml-0.5 flex h-3 w-3 shrink-0 items-center justify-center text-accent-600 dark:text-accent-400 [&>svg]:h-full [&>svg]:w-full">
          {icon}
        </span>
      ) : (
        <Hash className="ml-0.5 h-2.5 w-2.5 shrink-0 text-accent-500 dark:text-accent-400" />
      )}
      <span className="text-[9px] font-bold uppercase tracking-wider text-primary-600 dark:text-primary-300">
        {title}
      </span>
    </div>
  )

  const content =
    collapsible && isCollapsed ? null : (
      <div className="divide-y divide-primary-100 dark:divide-primary-800">
        {children}
      </div>
    )

  return (
    <div className={twMerge('', className)}>
      {headerContent}
      {content}
    </div>
  )
}
Section.displayName = 'DataSheet.Section'

/**
 * DataSheet.Row - Horizontal key-value pair
 */
const Row: React.FC<RowProps> = ({
  label,
  value,
  code,
  highlight = false,
  truncate = false,
  className,
}) => {
  const { variant } = useDataSheetContext()
  const isEmpty = value === undefined || value === null || value === ''
  const displayValue = isEmpty ? '—' : value

  return (
    <div
      className={twMerge(
        'group grid grid-cols-[minmax(120px,1fr)_minmax(0,2fr)] items-baseline gap-4 transition-colors hover:bg-primary-50/50 dark:hover:bg-primary-800/30',
        rowPaddingStyles[variant],
        highlight && 'bg-accent-50/30 dark:bg-accent-900/10',
        className,
      )}
    >
      {/* Label column */}
      <span
        className={twMerge(
          'text-xs font-semibold text-primary-600 transition-colors group-hover:text-primary-700 dark:text-primary-300 dark:group-hover:text-primary-200',
        )}
      >
        {label}
      </span>

      {/* Value column */}
      <div className="flex min-w-0 items-baseline justify-between gap-4">
        <span
          className={twMerge(
            'text-sm',
            truncate && 'truncate',
            isEmpty
              ? 'text-primary-300 dark:text-primary-600'
              : 'text-primary-900 dark:text-primary-100',
            highlight && 'font-medium text-accent-700 dark:text-accent-400',
          )}
        >
          {displayValue}
        </span>
        {code && (
          <span className="inline-flex shrink-0 items-center gap-1 text-xs text-primary-400 dark:text-primary-500">
            <span className="font-semibold uppercase tracking-wider">
              Code:
            </span>
            <span className="rounded border border-primary-200 bg-primary-100 px-1.5 py-0.5 font-mono font-medium text-primary-500 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-400">
              {code}
            </span>
          </span>
        )}
      </div>
    </div>
  )
}
Row.displayName = 'DataSheet.Row'

/**
 * DataSheet.EmptyState - Placeholder when no data
 */
const EmptyState: React.FC<EmptyStateProps> = ({
  children = 'No data available',
  className,
}) => {
  const { variant } = useDataSheetContext()

  return (
    <div
      className={twMerge(
        'flex items-center justify-center text-sm text-primary-400 dark:text-primary-500',
        rowPaddingStyles[variant],
        'py-8',
        className,
      )}
    >
      {children}
    </div>
  )
}
EmptyState.displayName = 'DataSheet.EmptyState'

// ============================================================================
// Main Component
// ============================================================================

/**
 * DataSheet - Container for spreadsheet-style data display
 */
const DataSheet: React.FC<DataSheetProps> & {
  Section: typeof Section
  Row: typeof Row
  EmptyState: typeof EmptyState
} = ({ children, title, icon, badge, variant = 'default', className }) => {
  const contextValue: DataSheetContextValue = { variant }

  const hasHeader = title || icon || badge

  return (
    <DataSheetContext.Provider value={contextValue}>
      <div className={twMerge(containerStyles[variant], className)}>
        {hasHeader && (
          <div className={headerStyles[variant]}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                {icon && (
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center text-accent-500 dark:text-accent-400 [&>svg]:h-full [&>svg]:w-full">
                    {icon}
                  </span>
                )}
                {title && (
                  <h3 className="text-xs font-bold uppercase tracking-wider text-primary-500 dark:text-primary-400">
                    {title}
                  </h3>
                )}
              </div>
              {badge && (
                <span className="rounded-full border border-primary-200 bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-500 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-400">
                  {badge}
                </span>
              )}
            </div>
          </div>
        )}
        <div className="divide-y divide-primary-100 dark:divide-primary-800">
          {children}
        </div>
      </div>
    </DataSheetContext.Provider>
  )
}

DataSheet.displayName = 'DataSheet'
DataSheet.Section = Section
DataSheet.Row = Row
DataSheet.EmptyState = EmptyState

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Converts ALL CAPS text to Title Case for friendlier display.
 * Handles common acronyms and edge cases.
 */
function toTitleCase(str: string | undefined): string | undefined {
  if (!str) return str
  // Only transform if the string appears to be all caps (or mostly caps)
  if (str !== str.toUpperCase()) return str

  return str
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bUs\b/g, 'US') // Fix "Us" -> "US"
    .replace(/\bU\.s\./gi, 'U.S.') // Fix U.S.
    .replace(/\bId\b/g, 'ID') // Fix "Id" -> "ID"
}

// ============================================================================
// Skeleton Component
// ============================================================================

interface DataSheetSkeletonProps {
  /** Number of rows to show */
  rows?: number
  /** Show header */
  showHeader?: boolean
  /** Number of sections */
  sections?: number
  variant?: DataSheetVariant
  className?: string
}

const DataSheetSkeleton: React.FC<DataSheetSkeletonProps> = ({
  rows = 4,
  showHeader = true,
  sections = 1,
  variant = 'default',
  className,
}) => {
  return (
    <div className={twMerge(containerStyles[variant], className)}>
      {showHeader && (
        <div className={headerStyles[variant]}>
          <div className="flex items-center gap-1.5">
            <div className="h-4 w-4 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
            <div className="h-3 w-28 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
          </div>
        </div>
      )}
      <div className="divide-y divide-primary-100 dark:divide-primary-800">
        {Array.from({ length: sections }).map((_, sectionIndex) => (
          <div key={sectionIndex}>
            {sections > 1 && (
              <div className="flex items-center gap-1.5 border-y border-primary-200 bg-primary-100/80 px-4 py-2 dark:border-primary-700 dark:bg-primary-800/80">
                <div className="h-2.5 w-2.5 animate-pulse rounded bg-primary-300 dark:bg-primary-600" />
                <div className="h-2 w-20 animate-pulse rounded bg-primary-300 dark:bg-primary-600" />
              </div>
            )}
            <div className="divide-y divide-primary-100 dark:divide-primary-800">
              {Array.from({ length: rows }).map((_, rowIndex) => (
                <div
                  key={rowIndex}
                  className={twMerge(
                    'grid grid-cols-[minmax(120px,1fr)_minmax(0,2fr)] gap-4',
                    rowPaddingStyles[variant],
                  )}
                >
                  <div className="h-3 w-20 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
                  <div className="h-4 w-32 animate-pulse rounded bg-primary-200 dark:bg-primary-700" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
DataSheetSkeleton.displayName = 'DataSheet.Skeleton'

// ============================================================================
// Row Data Type for External Use
// ============================================================================

/**
 * Standard row data structure for building DataSheet content.
 * Use this type when mapping domain data to DataSheet rows.
 */
export interface DataSheetRowData {
  label: string
  value: React.ReactNode
  code?: string
  highlight?: boolean
}

/**
 * Standard section data structure for building DataSheet content.
 * Use this type when mapping domain data to DataSheet sections.
 */
export interface DataSheetSectionData {
  title: string
  icon?: React.ReactNode
  rows: DataSheetRowData[]
}

/**
 * Render a DataSheet from section data.
 * Convenience component for the common pattern of mapping data to sections.
 */
interface DataSheetFromDataProps extends Omit<DataSheetProps, 'children'> {
  sections: DataSheetSectionData[]
}

const DataSheetFromData: React.FC<DataSheetFromDataProps> = ({
  sections,
  ...props
}) => {
  const nonEmptySections = sections.filter((s) => s.rows.length > 0)

  if (nonEmptySections.length === 0) return null

  return (
    <DataSheet {...props}>
      {nonEmptySections.map((section) => (
        <DataSheet.Section
          key={section.title}
          title={section.title}
          icon={section.icon}
        >
          {section.rows.map((row, index) => (
            <DataSheet.Row
              key={`${row.label}-${index}`}
              label={row.label}
              value={row.value}
              code={row.code}
              highlight={row.highlight}
            />
          ))}
        </DataSheet.Section>
      ))}
    </DataSheet>
  )
}
DataSheetFromData.displayName = 'DataSheet.FromData'

// ============================================================================
// Exports
// ============================================================================

export { DataSheet, DataSheetFromData, DataSheetSkeleton, toTitleCase }
export type {
  DataSheetProps,
  DataSheetVariant,
  SectionProps as DataSheetSectionProps,
  RowProps as DataSheetRowProps,
  EmptyStateProps as DataSheetEmptyStateProps,
  DataSheetSkeletonProps,
}
